import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse, AxiosError } from "axios";
import { UserGroupDTO } from "../types/userGroupDTO";
import Problem from "../types/problem";
import apiClient from "../main/apiClient";

const useFetchUserGroups = (organisationId: string) => {
    return useQuery<UserGroupDTO[], AxiosError>({
        queryKey: ["userGroup"],
        queryFn: () =>
            apiClient.get(`/organisation/${organisationId}/usergroup`).then((resp) => resp.data),
    });
}

const useFetchUserGroup = (organisationId: string, userGroupId: string) => {
    return useQuery<UserGroupDTO, AxiosError>({
        queryKey: ["userGroup", userGroupId],
        queryFn: () =>
            apiClient.get(`/organisation/${organisationId}/usergroup/${userGroupId}`).then((resp) => resp.data),
    });
};

const useUpdateUserGroup = (organisationId: string) => {
    const queryClient = useQueryClient();
    return useMutation<AxiosResponse, AxiosError<Problem>, UserGroupDTO>({
        mutationFn: (userGroupDTO) => apiClient.put(`/organisation/${organisationId}/usergroup`, userGroupDTO),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["userGroup"] });
        }
    });
};

const useAddUserGroup = (organisationId: string, setUserGroupState: (userGroup: UserGroupDTO) => void) => {
    const queryClient = useQueryClient();
    return useMutation<AxiosResponse, AxiosError<Problem>, UserGroupDTO>({
        mutationFn: (userGroupDTO) => apiClient.post(`/organisation/${organisationId}/usergroup`, userGroupDTO),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["userGroup"] });
        },
        onError: (error, variables) => {
            console.log(error);
            setUserGroupState(variables); // Preserve the state on error
        }
    });
};

const useDeleteUserGroup = (organisationId: string) => {
    const queryClient = useQueryClient();
    return useMutation<AxiosResponse, AxiosError<Problem>, UserGroupDTO>({
        mutationFn: (userGroupDTO) => apiClient.delete(`/organisation/${organisationId}/usergroup`, { data: userGroupDTO }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["userGroup"] });
            queryClient.invalidateQueries({ queryKey: ["user"] });
            queryClient.invalidateQueries({ queryKey: ["capabilitiesForUnits"] });
            queryClient.invalidateQueries({ queryKey: ["capabilitiesForUnitMission"] });
        }
    });
};

export { useFetchUserGroups, useFetchUserGroup, useUpdateUserGroup, useAddUserGroup, useDeleteUserGroup }
